import React, { ReactElement, useContext } from 'react'
import CLanguageHandler from '../../../NonReact/Languages/LanguageHandler'
import { LanguageContext } from '../../Common/Contexts'
import Heading from '../../Common/Components/Heading'
import P from '../../Common/Components/P'
import { PrintOutput as PrintOutputLanguage } from '../../../../i18n'
import Fieldset from '../../Common/Components/Fieldset'
import { Select, SelectOptions } from '../../Common/Components/Selects'
import FormRow from '../../Common/Components/FormRow'
import Checkbox from '../../Common/Components/Checkbox'
import ReportContentRowsSettings from '../Common/ShowRowsSettingsCheckboxes'

import {
    PRINT_OUTPUT_INCLUDES_OPTION_VALUE_CHART_ONLY,
    PRINT_OUTPUT_INCLUDES_OPTION_VALUE_TABLE_AND_CHART,
    PRINT_OUTPUT_INCLUDES_OPTION_VALUE_TABLE_AND_CHART_IF_AVAILABLE,
    PRINT_OUTPUT_INCLUDES_OPTION_VALUE_TABLE_ONLY
} from './Constants'
import { HeadingLevel } from '../../Common/Components/Heading/Heading'

// import './printTab.scss'

export type Props = {
    isPrintShowingHeaderRowEnabled: boolean
    setIsPrintShowingHeaderRowEnabled: (value: boolean) => void
    isPrintShowingFooterRowEnabled: boolean
    setIsPrintShowingFooterRowEnabled: (value: boolean) => void
    isPrintShowingContentRowsEnabled: boolean
    setIsPrintShowingContentRowsEnabled: (value: boolean) => void
    isPrintShowingSpacerRowsEnabled: boolean
    setIsPrintShowingSpacerRowsEnabled: (value: boolean) => void
    isShowSummaryHeaderEnabled: boolean
    setIsShowSummaryHeaderEnabled: (value: boolean) => void
    printViewChartInclusion: number
    setPrintViewChartInclusion: (value: number) => void
    isReportChartingFeatureEnabled: boolean
}

/**
 * Report Modal - Print Tab component.
 * @param isPrintShowingHeaderRowEnabled  Report property configuring Print View Show Header Row setting.
 * @param setIsShowingHeaderRowEnabled Setter for the property with identical name.
 * @param isPrintShowingFooterRowEnabled Report property configuring Print View Show Footer Row setting.
 * @param setIsPrintShowingFooterRowEnabled Setter for the property with identical name.
 * @param isPrintShowingContentRowsEnabled Report property configuring Print View Show Content Rows setting.
 * @param setIsPrintShowingContentRowsEnabled Setter for the property with identical name.
 * @param isPrintShowingSpacerRowsEnabled Report property configuring Print View Show Spacer Rows setting.
 * @param setIsPrintShowingSpacerRowsEnabled Setter for the property with identical name.
 * @param printViewChartInclusion Report property.
 * @param setPrintViewChartInclusion Report property setter.
 * @param isReportChartingFeatureEnabled  Report property configuring Print View Show Header Row setting.
 */
const PrintTab: React.FC<Props> = ({
    isPrintShowingHeaderRowEnabled,
    setIsPrintShowingHeaderRowEnabled,
    isPrintShowingFooterRowEnabled,
    setIsPrintShowingFooterRowEnabled,
    isPrintShowingContentRowsEnabled,
    setIsPrintShowingContentRowsEnabled,
    isPrintShowingSpacerRowsEnabled,
    setIsPrintShowingSpacerRowsEnabled,
    isShowSummaryHeaderEnabled,
    setIsShowSummaryHeaderEnabled,
    printViewChartInclusion,
    setPrintViewChartInclusion,
    isReportChartingFeatureEnabled
}: Props): ReactElement => {
    const language = useContext(LanguageContext) as CLanguageHandler
    language.loadBundle('PrintOutput')

    const printOutputIncludesOptions: SelectOptions<number> = [
        {
            label: `${language?.get(PrintOutputLanguage.TableAndChartIfAvailable)}`,
            value: PRINT_OUTPUT_INCLUDES_OPTION_VALUE_TABLE_AND_CHART_IF_AVAILABLE
        },
        {
            label: `${language?.get(PrintOutputLanguage.TableAndChart)}`,
            value: PRINT_OUTPUT_INCLUDES_OPTION_VALUE_TABLE_AND_CHART
        },
        {
            label: `${language?.get(PrintOutputLanguage.TableOnly)}`,
            value: PRINT_OUTPUT_INCLUDES_OPTION_VALUE_TABLE_ONLY
        },
        {
            label: `${language?.get(PrintOutputLanguage.ChartOnly)}`,
            value: PRINT_OUTPUT_INCLUDES_OPTION_VALUE_CHART_ONLY
        }
    ]

    return (
        <Fieldset className='print-output tab-area'>
            {/* Print Output Options section. */}
            <Heading level={HeadingLevel.ONE}>{language?.get(PrintOutputLanguage.PrintOutput_SectionHeader)}</Heading>
            {isReportChartingFeatureEnabled && <P>{language?.get(PrintOutputLanguage.Para1ChartingEnabled)}</P>}
            {!isReportChartingFeatureEnabled && <P>{language?.get(PrintOutputLanguage.Para1ChartingDisabled)}</P>}

            <FormRow isInline className={'print-output-includes'}>
                <Select
                    label={language?.get(PrintOutputLanguage.PrintableOutputIncludesLabel)}
                    options={printOutputIncludesOptions}
                    value={printViewChartInclusion}
                    handleChange={setPrintViewChartInclusion}
                />
                <Checkbox
                    className='left-aligned-checkbox'
                    onChange={setIsShowSummaryHeaderEnabled}
                    isChecked={isShowSummaryHeaderEnabled}
                    labelLeft={language.get(PrintOutputLanguage.ShowSummaryHeaderLabel)}
                />
            </FormRow>

            <P>{language.get(PrintOutputLanguage.Para2)}</P>

            <FormRow isInline className={'report-content-row-settings'}>
                <ReportContentRowsSettings
                    isShowingHeaderRowEnabled={isPrintShowingHeaderRowEnabled}
                    setIsShowingHeaderRowEnabled={setIsPrintShowingHeaderRowEnabled}
                    isShowingFooterRowEnabled={isPrintShowingFooterRowEnabled}
                    setIsShowingFooterRowEnabled={setIsPrintShowingFooterRowEnabled}
                    isShowingContentRowsEnabled={isPrintShowingContentRowsEnabled}
                    setIsShowingContentRowsEnabled={setIsPrintShowingContentRowsEnabled}
                    isShowingSpacerRowsEnabled={isPrintShowingSpacerRowsEnabled}
                    setIsShowingSpacerRowsEnabled={setIsPrintShowingSpacerRowsEnabled}
                    disableSettings={printViewChartInclusion === PRINT_OUTPUT_INCLUDES_OPTION_VALUE_CHART_ONLY}
                />
            </FormRow>
        </Fieldset>
    )
}

export default PrintTab
